import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { editEvaluator } from '@/services/evaluator/edit';
import { Evaluator } from '@/types/evaluator';
import { toast } from 'react-hot-toast';
import { X, Check } from 'lucide-react';

interface EditEvaluatorFormProps {
  evaluator: Evaluator;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function EditEvaluatorForm({
  evaluator,
  onSuccess,
  onCancel
}: EditEvaluatorFormProps) {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    firstName: evaluator.firstName,
    lastName: evaluator.lastName
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await editEvaluator(evaluator.id, currentUser.uid, {
        ...formData,
        email: evaluator.originalEmail || evaluator.email
      });
      onSuccess();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update evaluator');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 border-gray-300 focus:ring-primary"
            disabled={isSubmitting}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 border-gray-300 focus:ring-primary"
            disabled={isSubmitting}
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Email Address
        </label>
        <input
          type="email"
          value={evaluator.originalEmail || evaluator.email}
          className="w-full px-3 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
          disabled
        />
        <p className="mt-1 text-sm text-gray-500">Email cannot be changed</p>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex items-center px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
        >
          <X className="w-5 h-5 mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex items-center px-4 py-2 bg-primary text-dark rounded-lg hover:bg-primary-hover disabled:opacity-50"
        >
          <Check className="w-5 h-5 mr-2" />
          {isSubmitting ? 'Updating...' : 'Update Evaluator'}
        </button>
      </div>
    </form>
  );
}